// ===============================================================
// ProBank — Profile Page
//
// Simple account screen reached from the bottom navigation.
//
// SUBSCRIPTION VISIBILITY RULE (strict, matches the dashboard):
//   Show the Subscription row ONLY when BOTH are true:
//     1. user.hasEverFunded === true   (has funded at least once)
//     2. user.subscriptionActive !== true (not already subscribed)
//
//   - Never funded  → no Subscription row
//   - Already subscribed → no Subscription row
//
// User data is refreshed from the backend on every visit so the
// flags are never stale.
// ===============================================================

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";

export default function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    // 1) Instant render from cache
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      if (stored && (stored._id || stored.email || stored.name)) {
        setUser(stored);
      }
    } catch (err) {
      console.warn("Profile load error:", err);
    }

    // 2) Always refresh from the backend — hasEverFunded and
    //    subscriptionActive must be current on every visit.
    const refreshUser = async () => {
      try {
        const res = await API.get("/dashboard");
        const fresh = res.data?.user;
        if (fresh) {
          setUser(fresh);
          localStorage.setItem("user", JSON.stringify(fresh));
        }
      } catch (err) {
        console.warn("Profile refresh failed (using cached data):", err);
      }
    };

    refreshUser();
  }, []);

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
    } catch (err) {}
    window.location.href = "/login";
  };

  const name = user?.name || user?.fullName || "ProBank User";
  const email = user?.email || "—";
  const phone = user?.phone || "—";
  const country = user?.country || localStorage.getItem("userCountry") || "—";
  const plan = (user?.plan || "basic").toUpperCase();

  // STRICT RULE — identical to the dashboard's Subscribe button:
  // funded before AND not currently subscribed.
  const canSeeSubscription =
    user?.hasEverFunded === true && user?.subscriptionActive !== true;

  const initial = name.trim().charAt(0).toUpperCase() || "P";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#071032] to-black text-white pb-24">
      <div className="max-w-md mx-auto px-5 pt-8">

        {/* Avatar + name */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center text-3xl font-bold shadow-lg shadow-sky-500/20 mb-3">
            {initial}
          </div>
          <h1 className="text-xl font-bold">{name}</h1>
          <p className="text-slate-400 text-sm">{email}</p>
          <span className="mt-2 px-3 py-1 rounded-full text-xs font-semibold bg-sky-500/15 text-sky-300 border border-sky-500/30">
            {plan} PLAN
          </span>
        </div>

        {/* Details card */}
        <div className="rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/10 mb-6">
          <div className="flex justify-between px-4 py-3.5">
            <span className="text-slate-400 text-sm">Phone</span>
            <span className="text-sm font-medium">{phone}</span>
          </div>
          <div className="flex justify-between px-4 py-3.5">
            <span className="text-slate-400 text-sm">Country</span>
            <span className="text-sm font-medium">{country}</span>
          </div>
          <div className="flex justify-between px-4 py-3.5">
            <span className="text-slate-400 text-sm">Email</span>
            <span className="text-sm font-medium break-all text-right ml-4">{email}</span>
          </div>
        </div>

        {/* Quick links */}
        <div className="rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/10 mb-6">
          <button
            onClick={() => navigate("/cards")}
            className="w-full flex justify-between items-center px-4 py-3.5 text-left hover:bg-white/5 transition"
          >
            <span className="text-sm font-medium">My Virtual Card</span>
            <span className="text-slate-500">›</span>
          </button>

          {/* STRICT RULE: only funded-and-not-subscribed users ever
              see this row. Subscribed users never see it. */}
          {canSeeSubscription && (
            <button
              onClick={() => navigate("/subscription")}
              className="w-full flex justify-between items-center px-4 py-3.5 text-left hover:bg-white/5 transition"
            >
              <span className="text-sm font-medium">Subscription</span>
              <span className="text-slate-500">›</span>
            </button>
          )}

          <button
            onClick={() => navigate("/support")}
            className="w-full flex justify-between items-center px-4 py-3.5 text-left hover:bg-white/5 transition"
          >
            <span className="text-sm font-medium">Support Chat</span>
            <span className="text-slate-500">›</span>
          </button>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full py-3.5 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-400 font-semibold hover:bg-red-500/20 transition"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}
